/**
 * GlobalSearchModal.tsx — Command Palette global de Ceciluz Arte.
 *
 * Modal overlay con backdrop semitransparente + blur que se superpone al
 * contenido actual SIN cambiar de página. Cruza las 4 colecciones públicas
 * del proyecto y muestra los resultados agrupados por categoría.
 *
 * Datasets:
 *   · Terapias  — `useTherapies()` (features/therapies/api).
 *   · Galería   — `useGalleryItems()` (features/gallery/api).
 *   · Tienda    — `useProducts()` (features/shop/api).
 *   · Eventos   — `useEvents({ status: 'all' })` (features/events/api).
 *
 * Búsqueda:
 *   · Mínimo 2 caracteres antes de filtrar (evita ruido y queries de 1 char).
 *   · Match case-insensitive sobre `title`, `summary` y `description_md`.
 *   · Cap de 5 resultados por grupo para no saturar el panel.
 *
 * Accesibilidad:
 *   · `role="dialog"` + `aria-modal="true"` + `aria-labelledby`.
 *   · Autofocus en el input al abrir.
 *   · ESC cierra el modal.
 *   · Body scroll lock mientras está abierto.
 *   · Click en backdrop cierra.
 *   · Cada resultado es `<button>` con focus ring dorado del DS.
 *
 * Solo tokens del DS. Cero hex literals. Portal a `document.body` para
 * evitar stacking issues con el Navbar sticky o el MobileMenu.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';

import { useTherapies } from '../../features/therapies/api';
import { useProducts } from '../../features/shop/api';
import { useEvents } from '../../features/events/api';
import { useGalleryItems } from '../../features/gallery/api';
import { Routes } from '../../lib/routes';
import { cn } from '../../lib/cn';

// ---------------------------------------------------------------------------
// Tipos locales
// ---------------------------------------------------------------------------

/** Clave de grupo — driva la cabecera del agrupador y la ruta de destino. */
type GroupKey = 'therapies' | 'gallery' | 'shop' | 'events';

/**
 * Resultado unificado de cualquier dataset.
 *
 * Importante (QA UX 2026-06-08): el resultado SOLO guarda el `slug` del item,
 * NUNCA su `cover_url`, `image_url` ni ninguna URL absoluta de storage. La
 * ruta de destino se calcula en `buildRoutePath()` justo antes de navegar
 * con un `switch (groupKey)` explícito — esto blinda la navegación contra
 * cualquier URL de foto del storage que pudiera filtrarse por error.
 */
interface SearchResult {
  /** Id del row original (para `key` estable en React). */
  id: string;
  /** Título principal mostrado en negrita. */
  title: string;
  /** Resumen corto opcional (line-clamp 2). */
  summary: string | null;
  /**
   * Slug URL-safe del item (`a-z`, `0-9`, `-`). Único insumo usado para
   * construir la ruta interna. Si el slug del backend no cumple el patrón
   * el resultado se descarta antes de llegar a la lista — ver `isValidSlug`.
   */
  slug: string;
  /** Grupo al que pertenece — controla cabecera + path interno. */
  groupKey: GroupKey;
}

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

/** Mínimo de caracteres antes de disparar el filtrado. */
const MIN_QUERY_LENGTH = 2;
/** Resultados máximos por grupo para mantener el panel manejable. */
const MAX_RESULTS_PER_GROUP = 5;

/** Etiquetas visibles de cada cabecera. Define también el ORDEN de render. */
const GROUP_LABELS: Record<GroupKey, string> = {
  therapies: 'Terapias',
  gallery: 'Galería',
  shop: 'Tienda',
  events: 'Eventos',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Devuelve `true` si `needle` (ya en lowercase) aparece como substring en
 * la concatenación de cualquier campo de `parts`. Defensivo ante nulls.
 */
function matches(needle: string, parts: Array<string | null | undefined>): boolean {
  if (needle.length === 0) return false;
  const haystack = parts
    .filter((p): p is string => Boolean(p))
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

/**
 * Valida que un slug tenga forma URL-safe simple (a-z, A-Z, 0-9, guiones).
 * Rechaza:
 *   · `null` / `undefined` / cadena vacía.
 *   · URLs absolutas (`https://...`, `http://...`).
 *   · Paths con barras (`/foo/bar`, `gallery/foo.webp`).
 *   · Cualquier cosa con `://`, `.`, `?`, `#`, espacios, etc.
 *
 * Si un row del backend trae un campo `slug` corrupto (escenario teórico,
 * pero posible si el admin escribe algo raro), su resultado NO entra al
 * panel — preferimos omitir el item antes que arriesgar una URL malformada.
 */
function isValidSlug(value: string | null | undefined): value is string {
  if (!value || typeof value !== 'string') return false;
  return /^[a-zA-Z0-9-]+$/.test(value);
}

/**
 * Construye el path interno del router a partir del grupo + slug.
 *
 * Reemplazo explícito del enfoque anterior donde el `href` se almacenaba
 * pre-construido en cada `SearchResult`. Ahora el `switch` vive en el
 * único punto donde se navega — imposible que un campo distinto al `slug`
 * (p.ej. `cover_url`, `image_url`) se filtre como destino del router.
 *
 * Estas rutas reflejan exactamente las funciones de `Routes` en
 * `lib/routes.ts` para una sola fuente de verdad:
 *   therapies → Routes.therapyDetail(slug)  = `/terapias/${slug}`
 *   shop      → Routes.productDetail(slug)  = `/tienda/${slug}`
 *   events    → Routes.eventDetail(slug)    = `/eventos/${slug}`
 *   gallery   → Routes.galleryItem(slug)    = `/galeria#${slug}`
 */
function buildRoutePath(groupKey: GroupKey, slug: string): string {
  switch (groupKey) {
    case 'therapies':
      return Routes.therapyDetail(slug);
    case 'shop':
      return Routes.productDetail(slug);
    case 'events':
      return Routes.eventDetail(slug);
    case 'gallery':
      return Routes.galleryItem(slug);
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface GlobalSearchModalProps {
  /** Controla la visibilidad del modal. */
  isOpen: boolean;
  /** Callback que la implementación padre dispara al cerrar. */
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function GlobalSearchModal({ isOpen, onClose }: GlobalSearchModalProps) {
  const [query, setQuery] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const titleId = useId();

  // Datasets — los hooks se montan junto con el componente. Cuando el modal
  // está cerrado el componente no se renderiza (`if (!isOpen) return null`
  // abajo), así que las queries no corren en background.
  const therapiesQuery = useTherapies();
  const productsQuery = useProducts();
  const eventsQuery = useEvents({ status: 'all' });
  const galleryQuery = useGalleryItems();

  // Autofocus al abrir — `setTimeout(0)` garantiza que el DOM ya está en
  // el árbol cuando llamamos a `focus()`.
  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  // ESC cierra + body scroll lock.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  // Reset query cada vez que el modal se cierra — el siguiente open arranca
  // limpio sin recuerdos de la sesión anterior.
  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  /**
   * Pipeline de búsqueda. Reactivo ante cualquier cambio en query o en los
   * datasets cacheados por TanStack Query (si llegan nuevos datos mientras
   * el modal está abierto, los resultados se refrescan).
   */
  const grouped = useMemo<Record<GroupKey, SearchResult[]>>(() => {
    const empty: Record<GroupKey, SearchResult[]> = {
      therapies: [],
      gallery: [],
      shop: [],
      events: [],
    };
    const needle = query.trim().toLowerCase();
    if (needle.length < MIN_QUERY_LENGTH) return empty;

    // En cada bucket guardamos SOLO `id`, `title`, `summary` y `slug`. La
    // ruta de destino NO se construye aquí — se calcula en `buildRoutePath`
    // al momento de navegar (handler `handleSelect`). Esto evita por completo
    // la posibilidad de filtrar un `cover_url` u otra URL absoluta hacia el
    // router.
    //
    // `isValidSlug` descarta cualquier item cuyo slug del backend no sea
    // estrictamente URL-safe: si llegara una cadena con `://`, paths o
    // extensiones de archivo, el item se omite del panel.
    for (const t of therapiesQuery.data ?? []) {
      if (empty.therapies.length >= MAX_RESULTS_PER_GROUP) break;
      if (isValidSlug(t.slug) && matches(needle, [t.title, t.summary, t.description_md])) {
        empty.therapies.push({
          id: t.id,
          title: t.title,
          summary: t.summary,
          slug: t.slug,
          groupKey: 'therapies',
        });
      }
    }

    for (const item of galleryQuery.data ?? []) {
      if (empty.gallery.length >= MAX_RESULTS_PER_GROUP) break;
      if (isValidSlug(item.slug) && matches(needle, [item.title, item.description_md])) {
        empty.gallery.push({
          id: item.id,
          title: item.title,
          summary: item.description_md,
          slug: item.slug,
          groupKey: 'gallery',
        });
      }
    }

    for (const p of productsQuery.data ?? []) {
      if (empty.shop.length >= MAX_RESULTS_PER_GROUP) break;
      if (isValidSlug(p.slug) && matches(needle, [p.title, p.summary, p.description_md])) {
        empty.shop.push({
          id: p.id,
          title: p.title,
          summary: p.summary,
          slug: p.slug,
          groupKey: 'shop',
        });
      }
    }

    for (const e of eventsQuery.data ?? []) {
      if (empty.events.length >= MAX_RESULTS_PER_GROUP) break;
      if (isValidSlug(e.slug) && matches(needle, [e.title, e.summary, e.description_md])) {
        empty.events.push({
          id: e.id,
          title: e.title,
          summary: e.summary,
          slug: e.slug,
          groupKey: 'events',
        });
      }
    }

    return empty;
  }, [query, therapiesQuery.data, productsQuery.data, eventsQuery.data, galleryQuery.data]);

  const totalResults =
    grouped.therapies.length + grouped.gallery.length + grouped.shop.length + grouped.events.length;

  const needle = query.trim();
  const showHint = needle.length < MIN_QUERY_LENGTH;
  const showEmpty = !showHint && totalResults === 0;
  const isLoadingAny =
    therapiesQuery.isLoading ||
    productsQuery.isLoading ||
    eventsQuery.isLoading ||
    galleryQuery.isLoading;

  /**
   * Navega al item seleccionado y cierra el modal.
   *
   * Construcción del path:
   *  1. `buildRoutePath(groupKey, slug)` aplica un `switch` por categoría
   *     y devuelve una ruta interna usando `Routes.*` (NUNCA una URL del
   *     storage). Cobertura exhaustiva — TS garantiza que las 4 ramas
   *     están cubiertas en compile time.
   *  2. Guardia final de runtime: el path debe empezar con `/` y no
   *     contener `://` (que indicaría una URL absoluta). Si la guardia
   *     falla, el handler es no-op silencioso — preferimos no navegar
   *     antes que mandar al usuario a un asset de imagen.
   *
   * Orden cerrar→navegar: cerramos primero para que la transición de
   * router no pase por encima del backdrop blur.
   */
  function handleSelect(result: SearchResult) {
    const path = buildRoutePath(result.groupKey, result.slug);
    if (!path.startsWith('/') || path.includes('://')) {
      // Defensa imposible-en-teoría: `Routes.*` y `isValidSlug` ya garantizan
      // este invariante. Si llegamos aquí, hay un bug aguas arriba — no
      // navegamos para no exponer al usuario a una URL inesperada.
      return;
    }
    onClose();
    navigate(path);
  }

  if (!isOpen) return null;

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-modal flex items-start justify-center p-4 pt-[8vh] sm:p-6 md:pt-[10vh]"
    >
      {/* Backdrop — botón invisible para semántica de cierre por click. */}
      <button
        type="button"
        aria-label="Cerrar búsqueda"
        onClick={onClose}
        className="absolute inset-0 bg-primary-900/60 backdrop-blur-md"
      />

      {/* Panel.
          Z-index implícito: por estar después del backdrop en el árbol y
          `relative`, queda por encima sin necesidad de z-index explícito. */}
      <div
        className={cn(
          'relative w-full max-w-2xl overflow-hidden',
          'rounded-xl border border-gold-500/40 bg-cream-50 shadow-elev2',
        )}
      >
        {/* Header: lupa + input grande + botón cerrar */}
        <div className="flex items-center gap-3 border-b border-cream-200 px-4 py-3">
          <Search
            className="h-5 w-5 shrink-0 text-primary-700"
            aria-hidden="true"
            strokeWidth={1.5}
          />
          <input
            ref={inputRef}
            id={titleId}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder="Buscar terapias, productos, eventos…"
            autoComplete="off"
            aria-label="Buscar en todo el sitio"
            className={cn(
              'flex-1 min-w-0 bg-transparent',
              'font-display text-h4 text-primary-900',
              'placeholder:text-primary-900/40',
              'focus:outline-none',
            )}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar búsqueda"
            className={cn(
              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-pill',
              'text-primary-700 transition-colors duration-2 ease-out',
              'hover:bg-cream-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50',
            )}
          >
            <X className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />
          </button>
        </div>

        {/* Body: hint / loading / empty / resultados agrupados */}
        <div
          className="max-h-[70vh] overflow-y-auto px-2 py-2"
          aria-live="polite"
          aria-busy={isLoadingAny || undefined}
        >
          {showHint ? (
            <p className="px-4 py-6 text-center text-caption text-primary-900/60">
              Empieza a escribir para buscar en terapias, galería, tienda y eventos.
            </p>
          ) : isLoadingAny ? (
            <p className="px-4 py-6 text-center text-caption text-primary-900/60">Buscando…</p>
          ) : showEmpty ? (
            <p className="px-4 py-6 text-center text-caption text-primary-900/70">
              Sin resultados para “{needle}”. Prueba con otra palabra clave.
            </p>
          ) : (
            <ul className="flex flex-col gap-1" role="list">
              {(Object.keys(GROUP_LABELS) as GroupKey[]).map((key) => {
                const items = grouped[key];
                if (items.length === 0) return null;
                return (
                  <li key={key} className="flex flex-col">
                    <h3
                      className={cn(
                        'px-3 pb-1 pt-3',
                        'text-caption font-semibold uppercase tracking-[0.1em]',
                        'text-primary-700/70',
                      )}
                    >
                      {GROUP_LABELS[key]}
                    </h3>
                    <ul className="flex flex-col" role="list">
                      {items.map((item) => (
                        <li key={`${key}-${item.id}`}>
                          <button
                            type="button"
                            onClick={() => handleSelect(item)}
                            className={cn(
                              'flex w-full flex-col gap-0.5 rounded-md px-3 py-2 text-left',
                              'transition-colors duration-2 ease-out',
                              'hover:bg-cream-100',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50',
                            )}
                          >
                            <span className="line-clamp-1 font-display text-body text-primary-700">
                              {item.title}
                            </span>
                            {item.summary && (
                              <span className="line-clamp-2 text-caption text-primary-900/70">
                                {item.summary}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
